import React, { useState, useEffect } from 'react';
import { X, Calendar, MapPin, FileText, Info, CreditCard, ChevronLeft, ChevronRight, CheckSquare, ExternalLink, Square } from 'lucide-react';
import useBlockedDates from '../../hooks/useBlockedDates';
import { UIVenue } from '../../utils/venueTransformer';
import {
  getPrefixSuffixRatePerDay,
  getSecurityDeposit,
  getSecurityDepositPercent,
  getGstRate,
} from '../../utils/venueCharges';

interface BookingModalProps {
  show: boolean;
  onClose: () => void;
  selectedVenue: UIVenue | null;
  isMobile: boolean;
  theme: any;
  onProcessPayment: (formData: any) => void;
  isProcessingPayment: boolean;
  initialFromDate?: string;
  initialToDate?: string;
}

interface FormData {
  eventName: string;
  applicantName: string;
  mobile: string;
  email: string;
  gstNumber: string;
}

interface FormErrors {
  from?: string;
  to?: string;
  eventName?: string;
  applicantName?: string;
  mobile?: string;
  email?: string;
  termsAccepted?: string; // ✅ ADD THIS LINE
}

interface BookingDates {
  from: string;
  to: string;
}

const BookingModal: React.FC<BookingModalProps> = ({
  show,
  onClose,
  selectedVenue,
  isMobile,
  theme,
  onProcessPayment,
  isProcessingPayment,
  initialFromDate,
  initialToDate
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookingDates, setBookingDates] = useState<BookingDates>({ from: '', to: '' });
  const [selectionMode, setSelectionMode] = useState<'single' | 'range'>('single');
  const [formData, setFormData] = useState<FormData>({
    eventName: '',
    applicantName: '',
    mobile: '',
    email: '',
    gstNumber: ''
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  // Fetch blocked dates using the custom hook
const {
  blockedDates,
  loading: loadingBlockedDates,
  error: blockedDatesError
} = useBlockedDates(
  selectedVenue?.id,           // ✅ always pass ID
  currentMonth,
  show && !!selectedVenue,
  selectedVenue?.name          // ✅ pass name separately for skip check
);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setBookingDates({ from: '', to: '' });
      setFormData({
        eventName: '',
        applicantName: '',
        mobile: '',
        email: '',
        gstNumber: ''
      });
      setFormErrors({});
      setCurrentMonth(new Date());
      setTermsAccepted(false); // ✅ ADD THIS LINE
    setShowTermsModal(false); // ✅ ADD THIS LINE
    }
  }, [show]);

  // Set initial dates when coming from Availability Modal
  useEffect(() => {
    if (show && initialFromDate) {
      console.log('Setting initial dates:', { from: initialFromDate, to: initialToDate || initialFromDate });
      setBookingDates({
        from: initialFromDate,
        to: initialToDate || initialFromDate
      });
      
      // Set current month to the selected date's month
      const fromDate = new Date(initialFromDate);
      if (!isNaN(fromDate.getTime())) {
        setCurrentMonth(new Date(fromDate.getFullYear(), fromDate.getMonth(), 1));
      }
    }
  }, [show, initialFromDate, initialToDate]);

  if (!show || !selectedVenue) return null;

  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  // ✅ Charges come from `venue.metadata` (see src/utils/venueCharges.ts).
  // Prefix/suffix rate and security deposit are per-venue — never hardcode them.
  const calculateTotal = () => {
    if (!selectedVenue || !bookingDates.from || !bookingDates.to) 
      return { 
        rent: 0,
        prefixSuffixRent: 0,
        securityDeposit: 0,
        securityDepositPercent: undefined as number | undefined,
        gst: 0,
        total: 0,
        entryFee: 0,
        days: 0,
        prefixDays: 0,
        suffixDays: 0,
        prefixSuffixRatePerDay: 0
      };
    
    const fromParts = bookingDates.from.split('-');
    const from = new Date(parseInt(fromParts[0]), parseInt(fromParts[1]) - 1, parseInt(fromParts[2]));
    
    const toParts = bookingDates.to.split('-');
    const to = new Date(parseInt(toParts[0]), parseInt(toParts[1]) - 1, parseInt(toParts[2]));
    
    // Calculate number of days
    const days = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // ✅ MANDATORY AUTO-CALCULATE PREFIX/SUFFIX DAYS BASED ON BOOKING DURATION
    let prefixDays = 0;
    let suffixDays = 0;

    if (days >= 1 && days <= 2) {
      prefixDays = 0;
      suffixDays = 0;
    } else if (days >= 3 && days <= 7) {
      prefixDays = 1;
      suffixDays = 1;
    } else if (days >= 8 && days <= 15) {
      prefixDays = 2;
      suffixDays = 2;
    } else if (days > 15) {
      prefixDays = 3;
      suffixDays = 3;
    }
    
    let baseRent = 0;
    let entryFee = 0;

    if (selectedVenue.type === 'event') {
      baseRent = selectedVenue.rate * days;
    } else {
      baseRent = selectedVenue.rate;

      if (selectedVenue.name === 'Lumbini Park' && selectedVenue.entryFee) {
        entryFee = selectedVenue.entryFee;
      }
    }

    // ✅ Per-venue deposit: flat, or a percentage of the venue rent when the
    // venue publishes `securityDepositPercent`. Percent applies to baseRent only
    // — not to prefix/suffix rent or GST.
    const securityDeposit = getSecurityDeposit(selectedVenue, baseRent);
    const securityDepositPercent = getSecurityDepositPercent(selectedVenue);

    // ✅ Prefix/Suffix rent at the venue's published per-day rate (auto-calculated)
    const prefixSuffixRatePerDay = getPrefixSuffixRatePerDay(selectedVenue);
    const prefixSuffixRent = (prefixDays + suffixDays) * prefixSuffixRatePerDay;

    // ✅ TOTAL RENT = Base Venue Rent + Prefix/Suffix Rent
    // GST applies to rent only — the security deposit is not taxed.
    const totalRent = baseRent + prefixSuffixRent;
    const gst = totalRent * getGstRate(selectedVenue);
    const total = totalRent + securityDeposit + gst + entryFee;
    
    return { 
      rent: baseRent,
      prefixSuffixRent,
      securityDeposit,
      securityDepositPercent,
      gst,
      entryFee,
      total,
      days,
      prefixDays,
      suffixDays,
      prefixSuffixRatePerDay
    };
  };

  const validateForm = () => {
    const errors: FormErrors = {};

    console.log('Validating form with data:', {
      bookingDates,
      formData
    });

    if (!bookingDates.from) {
      errors.from = 'Booking start date is required';
      console.log('ERROR: Missing from date');
    }
    
    if (!bookingDates.to) {
      errors.to = 'Booking end date is required';
      console.log('ERROR: Missing to date');
    }

    if (bookingDates.from && bookingDates.to) {
      const from = new Date(bookingDates.from);
      const to = new Date(bookingDates.to);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      console.log('Date validation:', {
        from: bookingDates.from,
        to: bookingDates.to,
        fromParsed: from,
        toParsed: to,
        fromValid: !isNaN(from.getTime()),
        toValid: !isNaN(to.getTime())
      });

      if (isNaN(from.getTime())) {
        errors.from = 'Invalid start date';
        console.log('ERROR: Invalid from date');
      } else if (from < today) {
        errors.from = 'Start date cannot be in the past';
        console.log('ERROR: From date is in the past');
      }

      if (isNaN(to.getTime())) {
        errors.to = 'Invalid end date';
        console.log('ERROR: Invalid to date');
      } else if (to < from) {
        errors.to = 'End date must be after start date';
        console.log('ERROR: To date is before from date');
      }

      // Check if any date in range is blocked
      if (from && to && !isNaN(from.getTime()) && !isNaN(to.getTime())) {
        let hasBlockedDate = false;
        const currentDate = new Date(from);
        while (currentDate <= to) {
          const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
          if (blockedDates.has(dateStr)) {
            hasBlockedDate = true;
            console.log('ERROR: Blocked date found in range:', dateStr);
            break;
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        if (hasBlockedDate) {
          errors.from = 'Selected dates contain blocked dates';
          errors.to = 'Please choose different dates';
        }
      }
    }

    if (!formData.eventName?.trim()) {
      errors.eventName = 'Event name is required';
      console.log('ERROR: Missing event name');
    }

    if (!formData.applicantName?.trim()) {
      errors.applicantName = 'Applicant name is required';
      console.log('ERROR: Missing applicant name');
    }

    if (!formData.mobile?.trim()) {
      errors.mobile = 'Mobile number is required';
      console.log('ERROR: Missing mobile');
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
      console.log('ERROR: Invalid mobile format:', formData.mobile);
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
      console.log('ERROR: Missing email');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      console.log('ERROR: Invalid email format:', formData.email);
    }
if (!termsAccepted) {
  errors.termsAccepted = 'You must accept the terms and conditions to proceed';
  console.log('ERROR: Terms not accepted');
}
    console.log('Validation errors:', errors);
    console.log('Validation result:', Object.keys(errors).length === 0 ? 'PASS' : 'FAIL');

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDateClick = (dateStr: string) => {
    // Check if date is blocked
    if (blockedDates.has(dateStr)) {
      alert('This date is not available. Please choose another date.');
      return;
    }

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clickedDate = new Date(dateStr);
    if (clickedDate < today) {
      alert('Cannot select past dates');
      return;
    }

    // Check if date is beyond 6 months
    const maxBookingDate = new Date();
    maxBookingDate.setMonth(maxBookingDate.getMonth() + 6);
    maxBookingDate.setHours(0, 0, 0, 0);
    if (clickedDate > maxBookingDate) {
      alert('Bookings can only be made up to 6 months in advance');
      return;
    }

    // Single mode - set both from and to to the same date
    if (selectionMode === 'single') {
      setBookingDates({ from: dateStr, to: dateStr });
      setFormErrors(prev => ({ ...prev, from: undefined, to: undefined }));
    } 
    // Range mode - two-click selection
    else {
      // First click: Set "from" date only
      if (!bookingDates.from || (bookingDates.from && bookingDates.to)) {
        setBookingDates({ from: dateStr, to: '' });
        setFormErrors(prev => ({ ...prev, from: undefined, to: undefined }));
      } 
      // Second click: Set "to" date (completing the range)
      else if (bookingDates.from && !bookingDates.to) {
        const fromDate = new Date(bookingDates.from);
        const toDate = new Date(dateStr);
        
        // Check if any date in range is blocked
        let hasBlockedDate = false;
        const startDate = fromDate < toDate ? fromDate : toDate;
        const endDate = fromDate < toDate ? toDate : fromDate;
        const currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
          const checkStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
          if (blockedDates.has(checkStr)) {
            hasBlockedDate = true;
            break;
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        if (hasBlockedDate) {
          alert('Selected range contains blocked dates. Please choose different dates.');
          return;
        }
        
        // Auto-swap if dates are in wrong order
        if (toDate < fromDate) {
          setBookingDates({ from: dateStr, to: bookingDates.from });
        } else {
          setBookingDates({ from: bookingDates.from, to: dateStr });
        }
        setFormErrors(prev => ({ ...prev, from: undefined, to: undefined }));
      }
    }
  };

  const handleProceedToPayment = () => {
    const isValid = validateForm();
    
    console.log('=== BOOKING MODAL SUBMISSION ===');
    console.log('Validation result:', isValid);
    console.log('Current formData:', formData);
    console.log('Current bookingDates:', bookingDates);
    console.log('================================');
    
    if (!isValid) {
      const firstErrorField = document.querySelector('[style*="border: 2px solid rgb(239, 68, 68)"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      alert('Please fill in all required fields correctly and accept the terms and conditions');

      return;
    }

    const costs = calculateTotal();
    
    // ✅ IMPORTANT: Pass the actual formData values with auto-calculated prefix/suffix
    const bookingPayload = {
      // Personal details from formData state
      eventName: formData.eventName.trim(),
      applicantName: formData.applicantName.trim(),
      mobile: formData.mobile.replace(/\D/g, ''),
      email: formData.email.trim().toLowerCase(),
      gstNumber: formData.gstNumber?.trim() || '',
      
      // Booking dates
      bookingDates: {
        from: bookingDates.from,
        to: bookingDates.to
      },
      
      // Additional days (auto-calculated from costs, not user input)
      prefixDays: costs.prefixDays,
      suffixDays: costs.suffixDays,
      
      // Cost breakdown
      costs: costs
    };

    console.log('📤 Sending booking payload to parent:', bookingPayload);
    
    // Pass to parent
    onProcessPayment(bookingPayload);
  };

  const { firstDay, daysInMonth } = getMonthDays(currentMonth);
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // Restrict calendar to next 6 months only
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const maxDate = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());
  const displayMonthNum = year * 12 + month;
  const currentMonthNum = now.getFullYear() * 12 + now.getMonth();
  const maxMonthNum = maxDate.getFullYear() * 12 + maxDate.getMonth();
  const isPrevMonthDisabled = displayMonthNum <= currentMonthNum;
  const isNextMonthDisabled = displayMonthNum >= maxMonthNum;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '24px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: theme.white,
          borderRadius: '24px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '32px',
            borderBottom: `2px solid ${theme.gray100}`,
            background: `linear-gradient(135deg, ${selectedVenue.color}15, ${selectedVenue.color}05)`,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: theme.gray900,
                marginBottom: '8px'
              }}
            >
              New Booking - {selectedVenue.name}
            </h2>
            <p style={{ color: theme.gray600, fontSize: '0.95rem' }}>
              <MapPin size={16} style={{ display: 'inline', marginRight: '6px' }} />
              {selectedVenue.location}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: theme.gray100,
              border: 'none',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.gray700
            }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ padding: '32px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
              gap: '32px'
            }}
          >
            {/* Left Column - Form */}
            <div>
              {/* Calendar Section */}
              <div
                style={{
                  background: `${selectedVenue.color}08`,
                  padding: '24px',
                  borderRadius: '16px',
                  marginBottom: '24px',
                  border: `2px solid ${selectedVenue.color}20`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: '800',
                      color: theme.gray900,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Calendar size={20} />
                    Availability Calendar
                  </h3>
                  
                  {/* Selection Mode Toggle */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setSelectionMode('single');
                        setBookingDates({ from: '', to: '' });
                      }}
                      style={{
                        padding: '6px 12px',
                        background: selectionMode === 'single' ? selectedVenue.color : theme.white,
                        color: selectionMode === 'single' ? theme.white : theme.gray700,
                        border: `2px solid ${selectionMode === 'single' ? selectedVenue.color : theme.gray200}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Single Day
                    </button>
                    <button
                      onClick={() => {
                        setSelectionMode('range');
                        setBookingDates({ from: '', to: '' });
                      }}
                      style={{
                        padding: '6px 12px',
                        background: selectionMode === 'range' ? selectedVenue.color : theme.white,
                        color: selectionMode === 'range' ? theme.white : theme.gray700,
                        border: `2px solid ${selectionMode === 'range' ? selectedVenue.color : theme.gray200}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Date Range
                    </button>
                  </div>
                </div>
                
                <p style={{ fontSize: '0.85rem', color: theme.gray600, marginBottom: '12px' }}>
                  <strong>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>
                  {' • '}
                  {selectionMode === 'single' && !bookingDates.from && '📅 Click a date to select'}
                  {selectionMode === 'single' && bookingDates.from && `✅ Selected: ${new Date(bookingDates.from).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  {selectionMode === 'range' && !bookingDates.from && '📅 Click start date'}
                  {selectionMode === 'range' && bookingDates.from && !bookingDates.to && '📅 Click end date to complete range'}
                  {selectionMode === 'range' && bookingDates.from && bookingDates.to && `✅ Selected: ${new Date(bookingDates.from).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} - ${new Date(bookingDates.to).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}`}
                </p>

                {/* Month Navigation */}
                <div
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <button
                    onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
                    disabled={isPrevMonthDisabled}
                    style={{
                      background: theme.white,
                      border: `2px solid ${theme.gray200}`,
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: isPrevMonthDisabled ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: isPrevMonthDisabled ? 0.4 : 1
                    }}
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>
                  <button
                    onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
                    disabled={isNextMonthDisabled}
                    style={{
                      background: theme.white,
                      border: `2px solid ${theme.gray200}`,
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: isNextMonthDisabled ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: isNextMonthDisabled ? 0.4 : 1
                    }}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>

                {/* Loading State */}
                {loadingBlockedDates && (
                  <div style={{ textAlign: 'center', padding: '40px', color: theme.gray600 }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        border: `4px solid ${theme.gray200}`,
                        borderTop: `4px solid ${selectedVenue.color}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }}
                    />
                    Loading availability...
                  </div>
                )}

                {/* Error State */}
                {blockedDatesError && (
                  <div
                    style={{
                      padding: '16px',
                      background: `${theme.error}15`,
                      border: `2px solid ${theme.error}40`,
                      borderRadius: '12px',
                      marginBottom: '16px',
                      color: theme.error
                    }}
                  >
                    Failed to load availability. Please try again.
                  </div>
                )}

                {/* Calendar Grid */}
                {!loadingBlockedDates && (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '8px',
                        textAlign: 'center'
                      }}
                    >
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div
                          key={day}
                          style={{
                            fontWeight: '700',
                            fontSize: '0.85rem',
                            color: theme.gray700,
                            padding: '8px 0'
                          }}
                        >
                          {day}
                        </div>
                      ))}
                      {/* Empty cells for days before month starts */}
                      {Array.from({ length: firstDay }, (_, i) => (
                        <div key={`empty-${i}`} />
                      ))}
                      {/* Calendar days */}
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const isBlocked = blockedDates.has(dateStr);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isPast = new Date(year, month, day) < today;
                        const isBeyondLimit = new Date(year, month, day) > maxDate;
                        const isDisabled = isBlocked || isPast || isBeyondLimit;
                        const isToday = new Date().getDate() === day &&
                                       new Date().getMonth() === month &&
                                       new Date().getFullYear() === year;

                        // Check if date is in selected range
                        const currentDate = new Date(dateStr);
                        const fromDate = bookingDates.from ? new Date(bookingDates.from) : null;
                        const toDate = bookingDates.to ? new Date(bookingDates.to) : null;

                        const isSelected = (bookingDates.from === dateStr) || (bookingDates.to === dateStr);
                        const isInRange = fromDate && toDate && currentDate >= fromDate && currentDate <= toDate;

                        return (
                          <button
                            key={day}
                            onClick={() => !isDisabled && handleDateClick(dateStr)}
                            disabled={isDisabled}
                            style={{
                              padding: '12px',
                              borderRadius: '8px',
                              background: isSelected
                                ? selectedVenue.color
                                : isInRange
                                ? `${selectedVenue.color}40`
                                : isBlocked
                                ? theme.error
                                : isPast || isBeyondLimit
                                ? theme.gray100
                                : isToday
                                ? `${selectedVenue.color}20`
                                : theme.white,
                              color: isSelected
                                ? theme.white
                                : isDisabled
                                ? theme.white
                                : theme.gray900,
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              fontWeight: isSelected || isToday ? '800' : '600',
                              fontSize: '0.9rem',
                              border: `2px solid ${isSelected ? selectedVenue.color : theme.gray200}`,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isDisabled && !isSelected) {
                                e.currentTarget.style.background = `${selectedVenue.color}40`;
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isDisabled && !isSelected && !isInRange) {
                                e.currentTarget.style.background = isToday ? `${selectedVenue.color}20` : theme.white;
                                e.currentTarget.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div
                      style={{
                        marginTop: '16px',
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                        fontSize: '0.85rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            background: selectedVenue.color,
                            borderRadius: '4px'
                          }}
                        />
                        <span>Selected</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            background: theme.error,
                            borderRadius: '4px'
                          }}
                        />
                        <span>Booked</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            background: theme.gray100,
                            borderRadius: '4px'
                          }}
                        />
                        <span>Past</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            background: theme.white,
                            border: `2px solid ${theme.gray200}`,
                            borderRadius: '4px'
                          }}
                        />
                        <span>Available</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Booking Details Form */}
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: '800',
                    color: theme.gray900,
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FileText size={20} />
                  Booking Details
                </h3>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {/* Date Inputs */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '16px'
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Booking From *
                      </label>
                      <input
                        type="date"
                        value={bookingDates.from}
                        onChange={(e) => {
                          setBookingDates({ ...bookingDates, from: e.target.value });
                          setFormErrors(prev => ({ ...prev, from: undefined }));
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.from ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.from && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.from}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Booking To *
                      </label>
                      <input
                        type="date"
                        value={bookingDates.to}
                        onChange={(e) => {
                          setBookingDates({ ...bookingDates, to: e.target.value });
                          setFormErrors(prev => ({ ...prev, to: undefined }));
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.to ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.to && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.to}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Event Name */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '0.9rem'
                      }}
                    >
                      Event Name *
                    </label>
                    <input
                      type="text"
                      placeholder="Enter event name"
                      value={formData.eventName}
                      onChange={(e) => {
                        setFormData({ ...formData, eventName: e.target.value });
                        setFormErrors(prev => ({ ...prev, eventName: undefined }));
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${formErrors.eventName ? theme.error : theme.gray200}`,
                        borderRadius: '8px',
                        fontSize: '0.95rem'
                      }}
                    />
                    {formErrors.eventName && (
                      <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                        {formErrors.eventName}
                      </p>
                    )}
                  </div>

                  {/* Applicant Name & Mobile */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '16px'
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Applicant Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={formData.applicantName}
                        onChange={(e) => {
                          setFormData({ ...formData, applicantName: e.target.value });
                          setFormErrors(prev => ({ ...prev, applicantName: undefined }));
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.applicantName ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.applicantName && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.applicantName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        placeholder="Enter mobile number"
                        value={formData.mobile}
                        onChange={(e) => {
                          setFormData({ ...formData, mobile: e.target.value });
                          setFormErrors(prev => ({ ...prev, mobile: undefined }));
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `2px solid ${formErrors.mobile ? theme.error : theme.gray200}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                      {formErrors.mobile && (
                        <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                          {formErrors.mobile}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '0.9rem'
                      }}
                    >
                      Email ID *
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setFormErrors(prev => ({ ...prev, email: undefined }));
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${formErrors.email ? theme.error : theme.gray200}`,
                        borderRadius: '8px',
                        fontSize: '0.95rem'
                      }}
                    />
                    {formErrors.email && (
                      <p style={{ color: theme.error, fontSize: '0.8rem', marginTop: '4px' }}>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  {/* GST Number */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        fontSize: '0.9rem'
                      }}
                    >
                      GST Number (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Enter GST number if applicable"
                      value={formData.gstNumber}
                      onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `2px solid ${theme.gray200}`,
                        borderRadius: '8px',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>
                </div>
              </div>


              {/* ✅ NEW: TERMS AND CONDITIONS ACCEPTANCE SECTION */}
                <div
                  style={{
                    background: `${theme.secondaryBlue}08`,
                    padding: '20px',
                    borderRadius: '16px',
                    border: `2px solid ${termsAccepted ? theme.success : formErrors.termsAccepted ? theme.error : theme.secondaryBlue}40`,
                    marginBottom: '24px'
                  }}
                >
                  <h3
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: theme.gray900,
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FileText size={20} style={{ color: theme.secondaryBlue }} />
                    Terms & Conditions
                  </h3>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '12px'
                    }}
                  >
                    <button
                      onClick={() => {
                        setTermsAccepted(!termsAccepted);
                        setFormErrors(prev => ({ ...prev, termsAccepted: undefined }));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0
                      }}
                    >
                      {termsAccepted ? (
                        <CheckSquare size={24} style={{ color: theme.success }} />
                      ) : (
                        <Square size={24} style={{ color: theme.gray400 }} />
                      )}
                    </button>
                    <div style={{ fontSize: '0.95rem', lineHeight: 1.6, color: theme.gray700 }}>
                      I have read and agree to the{' '}
                      <button
                        onClick={() => setShowTermsModal(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: theme.secondaryBlue,
                          fontWeight: '700',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 'inherit',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        Terms and Conditions
                        <ExternalLink size={14} />
                      </button>
                      {' '}for venue booking at {selectedVenue.name}.
                    </div>
                  </div>

                  {formErrors.termsAccepted && (
                    <div
                      style={{
                        padding: '12px',
                        background: `${theme.error}15`,
                        border: `2px solid ${theme.error}40`,
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        color: theme.error,
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Info size={16} />
                      {formErrors.termsAccepted}
                    </div>
                  )}

                  {termsAccepted && (
                    <div
                      style={{
                        padding: '12px',
                        background: `${theme.success}15`,
                        border: `2px solid ${theme.success}40`,
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        color: theme.success,
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <CheckSquare size={16} />
                      Terms and conditions accepted
                    </div>
                  )}
                </div>
            </div>

            {/* Right Column - UPDATED CHARGES SUMMARY */}
            <div>
              <div
                style={{
                  background: theme.white,
                  border: `2px solid ${selectedVenue.color}`,
                  borderRadius: '16px',
                  padding: '24px',
                  position: 'sticky',
                  top: '120px'
                }}
              >
                <h3
                  style={{
                    fontSize: '1.3rem',
                    fontWeight: '800',
                    color: theme.gray900,
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}
                >
                  CHARGES BREAKDOWN
                </h3>

                {(() => {
                  const costs = calculateTotal();
                  return (
                    <div style={{ display: 'grid', gap: '16px' }}>
                      
                      {/* VENUE RENT */}
                      <div
                        style={{
                          padding: '16px',
                          background: theme.gray50,
                          borderRadius: '12px'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: theme.gray600,
                            marginBottom: '4px'
                          }}
                        >
                          Venue Rent Amount
                        </div>
                        <div
                          style={{
                            fontSize: '1.5rem',
                            fontWeight: '800',
                            color: theme.gray900
                          }}
                        >
                          ₹{costs.rent.toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: theme.gray600,
                            marginTop: '4px'
                          }}
                        >
                          {selectedVenue.rateType} × {costs.days} day(s)
                        </div>
                      </div>

                      {/* PREFIX/SUFFIX RENT (Auto-calculated) - per-venue rate */}
                      {costs.prefixSuffixRent > 0 && (
                        <div
                          style={{
                            padding: '16px',
                            background: `${selectedVenue.color}15`,
                            borderRadius: '12px',
                            border: `2px solid ${selectedVenue.color}40`
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.85rem',
                              color: theme.gray600,
                              marginBottom: '4px'
                            }}
                          >
                            Prefix & Suffix Days (Auto-calculated)
                          </div>
                          <div
                            style={{
                              fontSize: '1.2rem',
                              fontWeight: '800',
                              color: theme.gray900
                            }}
                          >
                            ₹{costs.prefixSuffixRent.toLocaleString()}
                          </div>
                          <div
                            style={{
                              fontSize: '0.8rem',
                              color: theme.gray600,
                              marginTop: '4px'
                            }}
                          >
                            {costs.prefixDays} prefix + {costs.suffixDays} suffix day(s) @ ₹
                            {costs.prefixSuffixRatePerDay.toLocaleString()}/day
                          </div>
                        </div>
                      )}

                      {/* SECURITY DEPOSIT */}
                      <div
                        style={{
                          padding: '16px',
                          background: theme.gray50,
                          borderRadius: '12px'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: theme.gray600,
                            marginBottom: '4px'
                          }}
                        >
                          Security Deposit (Refundable)
                        </div>
                        <div
                          style={{
                            fontSize: '1.2rem',
                            fontWeight: '800',
                            color: theme.gray900
                          }}
                        >
                          ₹{costs.securityDeposit.toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: theme.gray600,
                            marginTop: '4px'
                          }}
                        >
                          {costs.securityDeposit > 0
                            ? costs.securityDepositPercent != null
                              ? `Refundable — ${costs.securityDepositPercent}% of rent, not taxed`
                              : 'Refundable — fixed per venue, not taxed'
                            : 'No security deposit for this venue'}
                        </div>
                      </div>

                      {/* ENTRY FEE (if applicable) */}
                      {costs.entryFee > 0 && (
                        <div
                          style={{
                            padding: '16px',
                            background: `${theme.warning}15`,
                            borderRadius: '12px',
                            border: `2px solid ${theme.warning}40`
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.85rem',
                              color: theme.gray600,
                              marginBottom: '4px'
                            }}
                          >
                            Entry Fee (Buddha Statue)
                          </div>
                          <div
                            style={{
                              fontSize: '1.2rem',
                              fontWeight: '800',
                              color: theme.gray900
                            }}
                          >
                            ₹{costs.entryFee.toLocaleString()}
                          </div>
                          <div
                            style={{
                              fontSize: '0.8rem',
                              color: theme.gray600,
                              marginTop: '4px'
                            }}
                          >
                            Lumbini Park - Buddha Statue area
                          </div>
                        </div>
                      )}

                      {/* GST */}
                      <div
                        style={{
                          padding: '16px',
                          background: theme.gray50,
                          borderRadius: '12px'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: theme.gray600,
                            marginBottom: '4px'
                          }}
                        >
                          GST (18%)
                        </div>
                        <div
                          style={{
                            fontSize: '1.2rem',
                            fontWeight: '800',
                            color: theme.gray900
                          }}
                        >
                          ₹{costs.gst.toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: theme.gray600,
                            marginTop: '4px'
                          }}
                        >
                          18% on total rent (excluding security deposit)
                        </div>
                      </div>

                      {/* AUTO-CALCULATION RULES INFO BOX — only for venues that levy a prefix/suffix charge */}
                      {costs.prefixSuffixRatePerDay > 0 && (
                      <div
                        style={{
                          padding: '16px',
                          background: `${theme.secondaryBlue}15`,
                          borderRadius: '12px',
                          border: `2px solid ${theme.secondaryBlue}40`,
                          fontSize: '0.85rem',
                          color: theme.gray700,
                          lineHeight: 1.6
                        }}
                      >
                        <div
                          style={{
                            fontWeight: '700',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: theme.secondaryBlue
                          }}
                        >
                          <Info size={16} />
                          PREFIX & SUFFIX RULES (Auto-Applied)
                        </div>
                        <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                          <li>1-2 days: No prefix/suffix</li>
                          <li>3-7 days: 1 prefix + 1 suffix (₹{(costs.prefixSuffixRatePerDay * 2).toLocaleString()})</li>
                          <li>8-15 days: 2 prefix + 2 suffix (₹{(costs.prefixSuffixRatePerDay * 4).toLocaleString()})</li>
                          <li>15+ days: 3 prefix + 3 suffix (₹{(costs.prefixSuffixRatePerDay * 6).toLocaleString()})</li>
                          <li><strong>Rate: ₹{costs.prefixSuffixRatePerDay.toLocaleString()} per prefix/suffix day</strong></li>
                        </ul>
                      </div>
                      )}

                      {/* TOTAL AMOUNT */}
                      <div
                        style={{
                          padding: '20px',
                          background: `linear-gradient(135deg, ${selectedVenue.color}, ${selectedVenue.color}dd)`,
                          borderRadius: '12px',
                          color: theme.white
                        }}
                      >
                        <div style={{ fontSize: '0.9rem', opacity: 0.95, marginBottom: '8px' }}>
                          TOTAL AMOUNT PAYABLE
                        </div>
                        <div style={{ fontSize: '2rem', fontWeight: '900' }}>
                          ₹{costs.total.toLocaleString()}
                        </div>
                      </div>

                      {/* SHOOTING NOTES */}
                      {selectedVenue.type === 'shooting' && (
                        <div
                          style={{
                            padding: '16px',
                            background: `${theme.warning}15`,
                            borderRadius: '12px',
                            border: `2px solid ${theme.warning}40`,
                            fontSize: '0.85rem',
                            color: theme.gray700,
                            lineHeight: 1.6
                          }}
                        >
                          <div
                            style={{
                              fontWeight: '700',
                              marginBottom: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: theme.warning
                            }}
                          >
                            <Info size={16} />
                            SHOOTING RATES
                          </div>
                          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                            <li>Rate shown is for 4 hours (6:00 AM - 6:00 PM)</li>
                            <li>After 6:00 PM charges will be <strong>DOUBLED</strong></li>
                            <li>For 8 hours: ₹{selectedVenue.rate8Hours?.toLocaleString() || '30,000'}</li>
                            {selectedVenue.name === 'Lumbini Park' && (
                              <li>Includes ₹5,000 entry fee for Buddha Statue area</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* ACTION BUTTONS */}
                      <button
                        onClick={handleProceedToPayment}
                        disabled={isProcessingPayment}
                        style={{
                          width: '100%',
                          background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.accentPurple})`,
                          color: theme.white,
                          border: 'none',
                          padding: '16px',
                          borderRadius: '12px',
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          marginTop: '16px',
                          opacity: isProcessingPayment ? 0.6 : 1
                        }}
                      >
                        {isProcessingPayment ? (
                          <>
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '3px solid white',
                                borderTop: '3px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard size={20} />
                            Proceed to Payment
                          </>
                        )}
                      </button>

                      <button
                        onClick={onClose}
                        style={{
                          width: '100%',
                          background: theme.white,
                          color: theme.gray700,
                          border: `2px solid ${theme.gray200}`,
                          padding: '12px',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* ✅ NEW: TERMS AND CONDITIONS MODAL */}
      {showTermsModal && selectedVenue && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '24px',
            overflowY: 'auto'
          }}
          onClick={() => setShowTermsModal(false)}
        >
          <div
            style={{
              background: theme.white,
              borderRadius: '20px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Terms Header */}
            <div
              style={{
                padding: '24px 32px',
                borderBottom: `2px solid ${theme.gray100}`,
                background: `linear-gradient(135deg, ${theme.secondaryBlue}15, ${theme.secondaryBlue}05)`,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: '1.8rem',
                    fontWeight: '800',
                    color: theme.gray900,
                    marginBottom: '4px'
                  }}
                >
                  Terms & Conditions
                </h2>
                <p style={{ color: theme.gray600, fontSize: '0.9rem' }}>
                  {selectedVenue.name} - {selectedVenue.type === 'event' ? 'Event Venue' : 'Shooting Location'}
                </p>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                style={{
                  background: theme.gray100,
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.gray700
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Terms Content */}
            <div style={{ padding: '32px' }}>
              {selectedVenue.termsAndConditions && selectedVenue.termsAndConditions.length > 0 ? (
                <ol
                  style={{
                    listStyleType: 'decimal',
                    paddingLeft: '24px',
                    color: theme.gray800,
                    fontSize: '0.95rem',
                    lineHeight: 1.8
                  }}
                >
                  {selectedVenue.termsAndConditions.map((term, index) => (
                    <li
                      key={index}
                      style={{
                        marginBottom: '16px',
                        paddingLeft: '8px'
                      }}
                    >
                      {term}
                    </li>
                  ))}
                </ol>
              ) : (
                <div
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: theme.gray500
                  }}
                >
                  <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                  <p style={{ fontSize: '1rem', fontWeight: '600' }}>
                    No specific terms available for this venue
                  </p>
                  <p style={{ fontSize: '0.9rem', marginTop: '8px' }}>
                    Standard HMDA booking terms and conditions apply
                  </p>
                </div>
              )}

              {/* Footer Note */}
              <div
                style={{
                  marginTop: '32px',
                  padding: '16px',
                  background: `${theme.info}10`,
                  border: `2px solid ${theme.info}40`,
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  color: theme.gray700,
                  lineHeight: 1.6
                }}
              >
                <strong style={{ color: theme.info }}>Note:</strong> By proceeding with the booking, you acknowledge that you have read and understood all terms and conditions listed above.
              </div>
            </div>

            {/* Terms Footer Actions */}
            <div
              style={{
                padding: '20px 32px',
                borderTop: `2px solid ${theme.gray100}`,
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                position: 'sticky',
                bottom: 0,
                background: theme.white
              }}
            >
              <button
                onClick={() => setShowTermsModal(false)}
                style={{
                  padding: '12px 24px',
                  background: theme.white,
                  color: theme.gray700,
                  border: `2px solid ${theme.gray200}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                  setFormErrors(prev => ({ ...prev, termsAccepted: undefined }));
                }}
                style={{
                  padding: '12px 24px',
                  background: `linear-gradient(135deg, ${theme.secondaryBlue}, ${theme.govBlue})`,
                  color: theme.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <CheckSquare size={18} />
                Accept & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingModal;